interface Section {
  description?: string;
  subsection?: Array<{ description?: string }>;
  table?: Array<{
    keyword: string;
    description: string;
  }>;
}

interface LegalProps {
  title: string;
  sections: Section[];
}

const content: LegalProps[] = [
  {
    title: "Our Terms and Conditions",
    sections: [
      {
        description:
          "These are the terms and conditions on which we supply products and services to you in accordance with below.",
      },
      {
        description:
          "Please read these terms carefully before you log in/sign up to the app and submit your booking and/or order to us. These terms tell you who we are, how we will provide products and services to you, how you and we may change or end the contract, what to do if there is a problem and other important information.",
      },
    ],
  },
  {
    title: "Definitions",
    sections: [
      {
        description:
          "The following definitions apply under these terms and conditions:",
      },
      {
        table: [
          {
            keyword: "App",
            description:
              "means our TRIM-IT customer mobile application software available for download from Apple App Store or Google Play Store.",
          },
          {
            keyword: "Mobile Barbers",
            description:
              "means our contractors (self-employed) and employees providing mobile barber services.",
          },
          {
            keyword: "Appointment",
            description:
              "means a service provided by a Mobile Barber in an allocated time slot.",
          },
          {
            keyword: "Pay As You Go",
            description:
              "means a booking made on a one-off basis for an Appointment. Travel fees are not included within the Pay As You Go fee but are added before confirmation of the Pay As You Go Appointment.",
          },
          {
            keyword: "Products",
            description: "means the products as set out in clause 3.5.",
          },
          {
            keyword: "Services",
            description:
              "means the services as set out in clause 3.5 which includes the services of a haircut from a Mobile Barber.",
          },
          {
            keyword: "Subscriptions",
            description:
              "means a 28 day rolling period where the customer subscribes to pay a fee for a set amount of Subscription Appointment Credits. Travel fees are not included within the Subscription fee but are added whilst booking a Subscription Appointment.",
          },
          {
            keyword: "Subscription Appointments",
            description:
              "means an Appointment made using the customer’s allocated Subscription Appointment Credits within their Subscription. The travel fees will be added before confirmation of the booking takes place and will be charged at that time.",
          },
          {
            keyword: "Subscription Appointment Credit",
            description:
              "means a credit that can be converted into a Subscription Appointment. One Subscription Appointment Credit can be converted to one Subscription Appointment for one person.",
          },
          {
            keyword: "Website",
            description: "means ​https://trimit.app",
          },
          {
            keyword: "Travel Fee",
            description:
              "means the fee for your Mobile Barber to come to your location. The price of your travel fee depends on the distance and time it takes to reach your location based on where the barber is throughout the given day. This fee is separate to the price for your Appointment, as such the fee is added to both Pay As You Go and Subscription Appointments during the booking process.",
          },
          {
            keyword: "Mobile Barbershop",
            description: "means the vehicle in which Appointments take place.",
          },
        ],
      },
    ],
  },
  {
    title: "Information about us",
    sections: [
      {
        description:
          "We are TRIM-IT SERVICES LIMITED a company registered in England and Wales. Our company registration number is 10180305 and our registered office is at Unit 52 St Olav's Court City Business Centre, Lower Road, London, England, SE16 2XB (“us”, “we”, or “our” for short). Any reference to “you” or “your” means you as the user of our Website or our App.",
      },
      {
        description:
          "You can contact us using the following methods and details:",
        subsection: [
          { description: "Email: ​hello@trimit.app" },
          { description: "In-app Messenger: ​https://trimit.app/download" },
          { description: "Website Messenger: https://trimit.app/" },
        ],
      },
      {
        description:
          "If we have to contact you, we will do so by telephone, by writing to you via our customer service platform or email. ",
      },
      {
        description:
          "When we use the words 'writing' or 'written' in these terms, this includes emails, letters or messages through the customer service platform.",
      },
      {
        description:
          "Our Website and App allow you to book and pay for a broad range of hair and beauty products (​Products​) and services (​Services​) from our Mobile Barbers.",
      },
      {
        description:
          "We provide the Mobile Barbers and are responsible for the Services provided by them, subject to these terms and conditions.",
      },
    ],
  },
  {
    title: "Orders, Appointments and our contracts with you",
    sections: [
      {
        description:
          "To create an order, booking or Subscription you must be over the age of 18.",
      },
      {
        description:
          "The attendee of the Appointment must be over the age of 18 or accompanied by their parent or guardian if under the age of 18. Should this not be the case, we may cancel your Appointment without a refund.",
      },
      {
        description:
          "The Mobile Barber Appointments we offer are in the below forms:",
        subsection: [
          {
            description:
              "Pay As You Go Appointments which are paid for upon booking the Appointment via the Website or our App, such Appointments can be made up to 3 (three) months maximum in advance; and",
          },
          {
            description:
              "Subscription Appointments which are booked using Subscription Appointment Credits. These credits are obtained by creating a Subscription: Subscription customers may redeem their allocated Subscription Appointment Credits within their 28 day Subscription Period to book Appointments up to three months in advance. Upon making each Appointment the customer shall pay Travel Fees to complete the booking on the TRIM-IT app or Website. For the avoidance of doubt, the Subscription Appointment Credits allocated in each 28 day period do not roll over into the next period and will need to be redeemed within the 28 days or the customer will lose the credits.",
          },
        ],
      },
      {
        description:
          "The booking process allows you to select dates and times, provide preferences (for which Mobile Barber) and the number of attendees for the Appointment. You can review and amend a booking before submitting, it is your responsibility to ensure that all details and information are correct before submitting your booking. You will only be able to book one Appointment at a time.",
      },
      {
        description:
          "Acceptance of your booking will take place when it appears on your Appointment screen on our Website or App showing the provider, estimated time, date, location and the payment made. This will create a contract between you and us, in relation to Pay As You Go Mobile Barber Appointments. The contract for Subscriptions is created upon payment being made for the Subscription. Any Appointments booked under Pay As You Go and Subscription can be made up to 3 months in advance. We endeavour to ensure all Appointments are on time but this is subject to unavoidable delays such as traffic, due to the nature of the Services provided.",
      },
      {
        description:
          "If we are unable to accept or accommodate your booking, we will inform you of this in writing or via our App and will not charge you.",
      },
      {
        description:
          "Our Website and App is solely for the promotion of the Services in the UK. Unfortunately, we do not provide Services to addresses outside the UK. The Services can only be provided in our operated areas where we have Mobile Barbers. The App and/or Website will not allow you to book for a location in which we do not operate.",
      },
      {
        description:
          "You may pay a discretionary tip to your Mobile Barber using the App but if you make a complaint about the Services or seek a refund for any reason whatsoever, you will not be able to claim back your tip.",
      },
    ],
  },
  {
    title: "Our Products",
    sections: [
      {
        description:
          "The images of the Products on our website are for illustrative purposes only. Although we have made every effort to display the colours accurately, we cannot guarantee that a device's display of the colours accurately reflects the colour of the Products. Your Product may vary slightly from those images.",
      },
      {
        description:
          "The packaging of the Product may vary from that shown in images on our website.",
      },
    ],
  },
  {
    title: "Your rights to make changes",
    sections: [
      {
        description:
          "If you wish to make a change to an Appointment, such as changing the time, date or location, please do so via one of the following:",
        subsection: [
          { description: "In-app Messenger: ​https://trimit.app/download" },
          { description: "Website Messenger: https://trimit.app/" },
          { description: "App - via the bookings page" },
        ],
      },
      {
        description:
          "You can reschedule your Appointment up to 48 hours before the Appointment time. If you do wish to change your Appointment, any change made will be subject to availability. If it is possible we will let you know about any changes to the price or anything else which would be necessary as a result of your requested change and ask you to confirm whether you wish to go ahead with the change. If we cannot make the change or the consequences of making the change are unacceptable to you, you may cancel the Appointment and elect to either receive a refund or credit on your account, which will be deducted automatically from your next payment.",
      },
      {
        description:
          "If you do not reschedule your Appointment by the 48 hours cut off period detailed above, we will retain the cost of your Appointment and you have no right to reschedule your Appointment nor will you receive a refund. It is imperative that you cancel through the App or Website by 48 hours otherwise you will need to book and pay for a new Appointment.",
      },
      {
        description:
          "If you are a subscriber and you do not reschedule your Appointment 48 hours prior to your Appointment, you will still be charged as if you attended the Appointment as part of your Subscription. If you cancel more than 48 hours before your Appointment your Subscription Appointment Credit will be returned to your Subscription and your Travel Fee will be refunded or credited to your account. All Subscription Appointment Credits must be redeemed within the current 28 day period as detailed under 4(1)(b) above. Should you no longer have a Subscription and get a refund for a Subscription Appointment Credit, you will have 28 days to use that Subscription Appointment Credit from time of refund.",
      },
    ],
  },
  {
    title: "Our rights to make changes",
    sections: [
      {
        description: "We may change the Services and/or Appointments:",
        subsection: [
          { description: "if our Mobile Barbers become unavailable;" },
          {
            description:
              "if there has been a delay by an event that is unforeseeable and out of our control;",
          },
          {
            description:
              "to reflect changes in relevant laws and regulatory requirements; and",
          },
          {
            description:
              "to implement minor technical adjustments and improvements, for example to address a security threat.",
          },
        ],
      },
      {
        description:
          "We will use all reasonable efforts to minimise the effect on your Appointments and the Services provided, but we may be required to reject pending Appointments and reschedule, edit or cancel Appointments in the event of any of the above events at clause 7.1 occurs all of which will be notified to you as soon as reasonably practicable.",
      },
    ],
  },
  {
    title: "Providing the Products and Services",
    sections: [
      {
        description:
          "Pay As You Go and Subscription prices do not include the  Travel Fee. The payment made upon booking also does not include parking costs and/or controls. It is your responsibility to ensure that (i) you can be contacted by the Mobile Barber and customer service on the date of your Appointment and prior to your Appointment; and (ii) there is free and adequate parking for the Mobile Barbershop to conduct your Appointment. If the address you book is on a restricted road (i.e. double yellow or red route) then the Mobile Barber will not park at that address and you will need to agree to an alternative address which is no more than 0.5 miles away from the initial address so that the Services can be provided. If we cannot find a suitable alternative address to park the Mobile Barbershop, then your Appointment will be cancelled and you will not receive a refund. For the purpose of safety to our Mobile Barbers, we do not knock on doors / buzz flat numbers of our customers so it is imperative that you are contactable and attend your Appointment at the Mobile Barbershop.",
      },
      {
        description:
          "It is your responsibility to ensure that the attendee is able to enter and exit the Mobile Barbershop independently and safely. Our mobile barbers are unable to provide assistance for getting into and out of the Mobile Barbershop, if this is required, you must prearrange your own assistance separately to your Appointment. Should you not be able to enter the Mobile Barbershop your Appointment may be cancelled and you may not receive a refund.",
      },
      {
        description:
          "If the Services provided by the Mobile Barber are delayed by an event outside of our control then we will contact you as soon as possible to let you know and we will take steps to minimise the effect of the delay. Provided we do this we will not be liable for delays caused by the event, but if there is a risk of substantial delay you may cancel the Appointment (whether it be a Pay As You Go or one of your Subscription Appointment Credits) and you may receive a refund if it is a Pay As You Go Appointment or your Subscription Appointment Credit returned, for any Services you have paid for but not received.",
      },
      {
        description:
          "If you have booked an Appointment with one of our Mobile Barbers and have provided an address for the provision of Services and you are not present at the address at the Appointment date and time (within 5 minutes of the appointed time or within 5 minutes of the Mobile Barbershop’s arrival, whichever is the later) we have the right to forfeit your Appointment and you will not be entitled to a refund.",
      },
      {
        description:
          "If your Mobile Barber arrives at the Appointment early (in which case you will be notified through our App) you have the right to still attend the Appointment at the appointed time and the 5 minute waiting time will be from the scheduled appointed time.",
      },
      {
        description:
          "We will need certain information from you so that we can confirm your Appointments and provide the Services to you, for example, personal details and bank account details. If you do not give us this information you will be unable to complete your Appointment and may lose your selected Appointment. If you give us incomplete or incorrect information, we may end the contract. You will be responsible for any delay, amendment or variation of the Services provided by the Mobile Barber or rescheduling of an Appointment if this is caused by you giving us incorrect or incomplete information.",
      },
      {
        description: "We may have to suspend the supply of the Services:",
        subsection: [
          {
            description:
              "To deal with technical problems or make minor technical changes;",
          },
          {
            description:
              "if our Mobile Barbers are no longer able to provide the Services in your location;",
          },
          {
            description:
              "to update the Services to reflect changes in relevant laws and regulatory requirements; and",
          },
          {
            description:
              "to make changes (subject to availability) to the Services as requested by you or notified by us to you (see clause 6).",
          },
        ],
      },
      {
        description:
          "We will contact you in advance to tell you we will be suspending the Services, unless the problem is urgent or an emergency. If we have to suspend the Mobile Barber Services for longer than 28 days we will adjust the price or suspend payment so that you do not pay for the Services while they are suspended and your Subscription will be paused. You may withdraw your Appointment, cancel your Appointment or your Subscription if we suspend the Services, or announce an upcoming suspension affecting your Appointment, in each case for a period of more than 28 days and we will refund any sums you have paid in advance for the Services in respect of the cancelled Appointments or the remaining Subscription Appointment Credits. For the avoidance of doubt, we will only refund you for any Appointments you have booked (or in the case of Subscriptions, you are in credit for) and have not received.",
      },
      {
        description:
          "If you do not pay for the Services when your invoice is due, you cannot book any new Appointments until payment has been made. We may cancel all Appointments and/or suspend your Subscription until you have paid us the outstanding amounts. We will contact you to tell you that we are cancelling your Appointments. We will not charge you for your Subscription during the period for which it is suspended. As well as suspending the Subscription we can also charge you interest on your overdue payments.",
      },
    ],
  },
  {
    title: "Your cancellation rights",
    sections: [
      {
        description:
          "You have the right to cancel an Appointment (whether it be Pay As You Go or Subscription) as follows:",
        subsection: [
          {
            description:
              "In accordance with clause 8.3 above on the day of the Appointment; and",
          },
          {
            description:
              "In the event of suspension by us in accordance with clause 8.8 above.",
          },
          { description: "With 48 hours notice prior to the Appointment" },
        ],
      },
      {
        description:
          " If you are cancelling a Pay As You Go Appointment for a reason set out in clause 9.1 above and/or in this clause 9.2 (a) to (c) below, the contract will terminate immediately. If you are cancelling an Appointment booked with a Subscription Appointment Credit, the Subscription Appointment Credit will be added to your current Subscription Appointment Credits and will need to be redeemed by the end of the current 28 day period.  If you had not made a Subscription Appointment in the current 28 day period and you exercise your right to cancel under this clause, we will refund you in full for any Services and/or Products which have not been provided. The reasons are:",
        subsection: [
          {
            description:
              "we have told you about an upcoming change to the Services (for example if we are no longer able to provide the Mobile Barber Services in your location);",
          },
          {
            description:
              "we have told you about an error in the price or description of the Services you have ordered and you do not wish to proceed;",
          },
          {
            description:
              "you have notified us that you wish to cancel either (i) up until 48 hours prior to your Appointment for the Pay As You Go; and/or (ii) at any time during your Subscription (subject to clause 11 below).",
          },
        ],
      },
      {
        description:
          "Nothing in these terms and conditions conflict or prohibit your legal rights and remedies under the Consumer Contracts Regulations 2013 and/or the Supply of Goods and Services Act (SGSA) 1982",
      },
      {
        description: "You do not have a right to refund for:",
        subsection: [
          { description: "Services that have been completed; and" },
          {
            description:
              "Products sealed for health protection or hygiene purposes, once these have been unsealed after you receive them.",
          },
        ],
      },
      {
        description:
          "Your cancellation rights are subject to the following deadlines:",
        subsection: [
          {
            description:
              "Pay As You Go Appointment - You have up until 48 hours prior to the Appointment to cancel, with a full refund or credit at your election. If you change your mind less than 48 hours prior to the Appointment, you are not entitled to a refund.",
          },
          {
            description:
              "Subscription - Your Subscription contract is on a 28 day rolling period (“Subscription Period”). You can cancel the contract anytime but if you have made an Appointment, you have to book using the outstanding Subscription Appointment Credits under your Subscription by the end of the 28 day period and your contract will be terminated. If you do not use any remaining Subscription Appointment Credits within the 28 day period, you will lose them and you will not be entitled to a refund. As stated above, Subscription Appointment Credits do not roll over from one 28 day period to the next. However, if you have not made any Subscription Appointments and you cancel your Subscription, you are entitled to a full refund for the current 28 day period in which you cancel within. For the avoidance of doubt, no refunds will be paid for any previous Subscription, irrespective of whether you have booked any Subscription Appointments or not.",
          },
          {
            description:
              "Subscription Appointments - You have anytime up until 48 hours prior to the Appointment to cancel, your Subscription Appointment Credit will be returned to you and your Travel Fee will be refunded or credited at your election. If you change your mind less than 48 hours prior to the Appointment, you are not entitled to a refund.",
          },
          {
            description:
              "Products - You have 14 days after the day you receive the Product, subject to clause 9.4 (b) above.",
          },
        ],
      },
      {
        description:
          "Cancelling an Appointment or your Subscription can be done via our website or mobile app, please let us know via one of the following:",
        subsection: [
          { description: "In-app Messenger: ​https://trimit.app/download" },
          { description: "App" },
          { description: "Website" },
        ],
      },
      {
        description:
          "If you change your mind after Products have been dispatched to you or you have received them, you must return them to us (within the 14 day period). You must post them back to us in its packaging (including sealed) at Unit 52, St Olav’s Court, Lower Road, London, SE16 2XB. Please email us at ​hello@trimit.app for a return label.",
      },
      {
        description: "We will pay the costs of return:",
        subsection: [
          { description: "if the Products are faulty; or" },
          {
            description:
              "if there is an error in pricing or description of the Product.",
          },
        ],
      },
      {
        description:
          "In all other circumstances (including where you are exercising your right to change your mind) you must pay the costs of return.",
      },
      {
        description:
          "Any refund owed to you will be paid, by the method you used for payment.",
      },
      {
        description:
          "In accordance with this clause 9, any refunds we make to you will be processed within 14 days of the cancellation date, subject to clause 9.8 above for Products.",
      },
    ],
  },
  {
    title: "Subscription Changes",
    sections: [
      {
        description:
          "If you are using a first time promotion, you cannot cancel your Subscription until either your first Appointment has been completed or you have not made any Appointments using your Subscription Appointment Credits in the 28 day period from when you signed up and your Subscription was confirmed. Cancellations for first time promotions are still subject to the cancellation deadlines set out above in clause 9.5.",
      },
      {
        description:
          "Your Subscription Appointment Credits reset every 28 days. Please note that Subscription Appointment Credits do not roll over to the next 28 day Subscription period and you will not be refunded if you do not redeem your Subscription Appointment Credits.",
      },
      {
        description: "Changing your Subscription plan:",
        subsection: [
          {
            description:
              "If you downgrade your Subscription to a lesser amount of Subscription Appointment Credits, you will continue with your current plan until the end of your current 28 day period, keeping your existing amount of Subscription Appointment Credits. Upon Subscription renewal, the new price will be charged to you and the new amount of Subscription Appointment Credits will be made available.",
          },
          {
            description:
              "If you upgrade your Subscription, the change will be effective immediately and you will be entitled to receive your upgraded package (i.e. more Subscription Appointment Credits); and we will charge you the difference between your original package and the upgraded package immediately after you upgrade. Your billing cycle from the original package will remain the same. Upon renewal at the end of your 28 day cycle you will be charged the amount for the new Subscription.",
          },
        ],
      },
    ],
  },
  {
    title: "Our rights to end the contract",
    sections: [
      {
        description:
          "We may reject booking requests, and cancel Appointments/Subscriptions if:",
        subsection: [
          {
            description:
              "You do not make any payment to us when it is due; and/or",
          },
          {
            description:
              "where you have booked an Appointment with one of our Mobile Barbers and have provided an address for the provision of Services, but are not present at the address of the Appointment at the agreed date and time(s) (with the 5 minute waiting time allowance as detailed above at clause 8.3).",
          },
        ],
      },
      {
        description:
          "At any time and for any reason whatsoever, we have the right to let you know that we are going to stop providing any of the Services. We will let you know as soon as reasonably practicable in the event we will stop supplying the Products and Services and we will refund any sums you have paid in advance for any Products/Services which will not be provided.",
      },
    ],
  },
  {
    title: "If there is a problem with the Products and Services",
    sections: [
      {
        description:
          "If you have any questions or complaints about the Services and/or Products provided by our Mobile Barbers, please talk to the Mobile Barbers during your Appointment. It is your responsibility to ensure you are satisfied with the Services provided before the end of the Appointment so it can be addressed and resolved immediately.",
      },
      {
        description:
          "If you are unable to resolve any problems with the Mobile Barber, please contact us via one of the following:",
        subsection: [
          { description: "Email: ​hello@trimit.app" },
          {
            description: "In-app Messenger: ​https://trimit.app/download",
          },
          { description: "Website Messenger: https://trimit.app/" },
        ],
      },
    ],
  },
  {
    title: "Price and Payment",
    sections: [
      {
        description:
          "The price of the Services (which includes VAT) will be the price indicated on the final confirmation booking page on our Website and/or App.",
      },
      {
        description:
          "For Products, if the rate of VAT changes between your order date and the date we supply the product, we will adjust the rate of VAT that you pay, unless you have already paid for the Product in full before the change in the rate of VAT takes effect.",
      },
      {
        description: "Payments are due as follows:",
        subsection: [
          {
            description:
              "For Products and Services payment is taken immediately upon booking / order confirmation.",
          },
          {
            description:
              "For ​Pay As You Go Appointments, Subscription Appointments and Travel Fee, you must pay when you book the Appointment.",
          },
          {
            description:
              "For ​Subscriptions​, the first payment will be taken immediately upon confirmation and will be due every 28 days unless modified upon request.",
          },
        ],
      },
      {
        description:
          "If you think a payment is wrong please contact us promptly to let us know.",
      },
      {
        description:
          "Any refunds you are entitled to under these Terms of Service will be processed in the same form as the payment made for the Services. For example, if the transaction was made using credit it must be refunded as credit and cannot be converted into another payment type, if and similarly the transaction was made by card, it must be refunded to the same card.",
      },
    ],
  },
  {
    title: "Liability",
    sections: [
      {
        description:
          "We are not liable to you and/or any other person including for loss, damage or injury caused or suffered in connection with the Services and/or the Products, except for death or personal injury caused by our negligence and recoverable on that ground.",
      },
      {
        description:
          "The sole remedy you have for breach by us of any of our obligations under this Agreement is an action at law for damages, that such are adequate compensation, and that you shall not be entitled to rescission, injunction and/or other equitable relief.",
      },
    ],
  },
  {
    title: "Data Protection",
    sections: [
      {
        description:
          "We will only use your personal information as set out in our privacy policy:",
        subsection: [{ description: "https://trimit.app/privacy-policy/" }],
      },
    ],
  },
  {
    title: "Other important terms",
    sections: [
      {
        description:
          "We shall be entitled to assign or license the benefit of this Agreement in whole or part to any third party.",
      },
      {
        description:
          "This contract is between you and us. No other person shall have any rights to enforce any of its terms.",
      },
      {
        description:
          "Each of the paragraphs of these terms operates separately. If any court or relevant authority decides that any of them are unlawful, the remaining paragraphs will remain in full force and effect.",
      },
      {
        description:
          "This Agreement and all matters ancillary to it shall be governed by and construed in accordance with English law and the English courts will have exclusive jurisdiction.",
      },
    ],
  },
];

export default content;
